// feature/notion-popover-dropdowns — Notion-style popover primitive + Select / MultiSelect / Combobox
// variants. Builds on feature/notion-polish's portal/dismiss/spring patterns.
//
// All animations wrapped in @media (prefers-reduced-motion: no-preference)
// via the .popover className (defined in index.css).
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { personas as personasApi } from "../api/endpoints";
import { useDebounced } from "./useDebounced";

const GAP = 8;
const MAX_W = 320;

function safeAnchor(triggerRect, menuW, menuH) {
  const vw = window.innerWidth, vh = window.innerHeight;
  let top = triggerRect.bottom + GAP;
  let flipped = false;
  // Flip above if would overflow viewport bottom
  if (top + menuH > vh - 8 && triggerRect.top - GAP - menuH > 8) {
    top = triggerRect.top - GAP - menuH;
    flipped = true;
  }
  let left = triggerRect.left;
  // Right-align if would overflow right edge
  if (left + menuW > vw - 8) left = Math.max(8, triggerRect.right - menuW);
  return { top, left, flipped };
}

// =====================================================================
// Popover — base primitive. Renders an open/close trigger and portals the
// content next to it. Children receive `close()` so they can dismiss after
// a selection.
// =====================================================================
export function Popover({ trigger, children, minWidth = 200, disabled }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const t = triggerRef.current.getBoundingClientRect();
    const w = Math.min(MAX_W, Math.max(minWidth, t.width));
    const h = menuRef.current?.offsetHeight || 240;
    setPos({ ...safeAnchor(t, w, h), width: w });
  }, [open, minWidth]);

  useEffect(() => {
    if (!open) return;
    function onDocMouse(e) {
      if (menuRef.current?.contains(e.target)) return;
      if (triggerRef.current?.contains(e.target)) return;
      setOpen(false);
    }
    function onKey(e) { if (e.key === "Escape") setOpen(false); }
    document.addEventListener("mousedown", onDocMouse);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocMouse);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <>
      <span ref={triggerRef} style={{ display: "inline-block" }}>
        {typeof trigger === "function"
          ? trigger({ open, setOpen: (v) => !disabled && setOpen(v) })
          : trigger}
      </span>
      {open && pos && createPortal(
        <div
          ref={menuRef}
          className={"popover" + (pos.flipped ? " flip" : "")}
          style={{ top: pos.top, left: pos.left, width: pos.width, minWidth }}
        >
          {typeof children === "function" ? children({ close: () => setOpen(false) }) : children}
        </div>,
        document.body
      )}
    </>
  );
}

// =====================================================================
// Trigger button — shared chip-style trigger used by Select / MultiSelect.
// =====================================================================
function TriggerChip({ label, isPlaceholder, onClear, open, onClick, className }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={"popover-trigger " + (className || "")}
      data-open={open ? "true" : "false"}
      data-placeholder={isPlaceholder ? "true" : "false"}
    >
      <span className="lbl">{label}</span>
      {onClear && (
        <span className="x" onClick={(e) => { e.stopPropagation(); onClear(); }} aria-label="Limpiar">×</span>
      )}
      <span className="chev" aria-hidden="true">▾</span>
    </button>
  );
}

// =====================================================================
// PopoverSelect — single-select dropdown.
// =====================================================================
export function PopoverSelect({ value, options, onChange, placeholder = "—", searchable = false, allowClear = true, triggerClassName }) {
  const [q, setQ] = useState("");
  const selected = options.find(o => String(o.value) === String(value));
  const filtered = q
    ? options.filter(o => o.label.toLowerCase().includes(q.toLowerCase()))
    : options;

  return (
    <Popover
      minWidth={200}
      trigger={({ open, setOpen }) => (
        <TriggerChip
          label={selected ? selected.label : placeholder}
          isPlaceholder={!selected}
          onClear={allowClear && selected ? () => onChange(null) : null}
          open={open}
          onClick={() => setOpen(!open)}
          className={triggerClassName}
        />
      )}
    >
      {({ close }) => (
        <>
          {searchable && (
            <input
              autoFocus
              className="popover-search"
              placeholder="Buscar…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          )}
          <div className="popover-list">
            {!filtered.length && (
              <div className="popover-empty">Sin resultados</div>
            )}
            {filtered.map(o => {
              const isSelected = String(o.value) === String(value);
              return (
                <div
                  key={o.value}
                  className={"popover-item" + (isSelected ? " selected" : "")}
                  onClick={() => { onChange(o.value); close(); }}
                >
                  {o.icon && <span className="ic">{o.icon}</span>}
                  <div className="content">
                    <div className="lbl">{o.label}</div>
                    {o.description && <div className="desc">{o.description}</div>}
                  </div>
                  {isSelected && <span className="check">✓</span>}
                </div>
              );
            })}
          </div>
        </>
      )}
    </Popover>
  );
}

// =====================================================================
// PopoverMultiSelect — checkbox list, footer with Limpiar/Aplicar.
// =====================================================================
export function PopoverMultiSelect({ values = [], options, onChange, placeholder = "—", searchable = true, allowClear = true, triggerClassName }) {
  const [q, setQ] = useState("");
  const filtered = q
    ? options.filter(o => o.label.toLowerCase().includes(q.toLowerCase()))
    : options;

  let label = placeholder;
  if (values.length === 1) {
    const m = options.find(o => String(o.value) === String(values[0]));
    label = m ? m.label : placeholder;
  } else if (values.length > 1) {
    label = `${values.length} seleccionados`;
  }

  function toggle(v) {
    if (values.map(String).includes(String(v))) onChange(values.filter(x => String(x) !== String(v)));
    else onChange([...values, v]);
  }

  return (
    <Popover
      minWidth={220}
      trigger={({ open, setOpen }) => (
        <TriggerChip
          label={label}
          isPlaceholder={!values.length}
          onClear={allowClear && values.length ? () => onChange([]) : null}
          open={open}
          onClick={() => setOpen(!open)}
          className={triggerClassName}
        />
      )}
    >
      {({ close }) => (
        <>
          {searchable && (
            <input
              autoFocus
              className="popover-search"
              placeholder="Buscar…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          )}
          <div className="popover-list">
            {!filtered.length && (
              <div className="popover-empty">Sin resultados</div>
            )}
            {filtered.map(o => {
              const isSelected = values.map(String).includes(String(o.value));
              return (
                <div
                  key={o.value}
                  className={"popover-item" + (isSelected ? " selected" : "")}
                  onClick={() => toggle(o.value)}
                >
                  <input type="checkbox" checked={isSelected} readOnly className="cb" />
                  <span className="lbl">{o.label}</span>
                </div>
              );
            })}
          </div>
          {values.length > 0 && (
            <div className="popover-footer">
              <button className="ghost sm" onClick={() => onChange([])}>Limpiar</button>
              <button className="sm" onClick={close}>Aplicar</button>
            </div>
          )}
        </>
      )}
    </Popover>
  );
}

// =====================================================================
// PopoverCombobox — search-as-you-type. Local options OR async searchFn.
// =====================================================================
export function PopoverCombobox({ value, label, options, searchFn, onChange, placeholder = "—", allowClear = true, triggerClassName }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(options || []);
  const [busy, setBusy] = useState(false);
  const dq = useDebounced(q, 300);

  useEffect(() => {
    if (!searchFn) {
      const ql = (q || "").toLowerCase();
      setResults((options || []).filter(o => o.label.toLowerCase().includes(ql)));
      return;
    }
    let alive = true;
    setBusy(true);
    Promise.resolve(searchFn(dq))
      .then(r => { if (alive) setResults(r); })
      .finally(() => { if (alive) setBusy(false); })
      .catch(() => { if (alive) setResults([]); });
    return () => { alive = false; };
  }, [dq, options, searchFn, q]);

  return (
    <Popover
      minWidth={260}
      trigger={({ open, setOpen }) => (
        <TriggerChip
          label={label || placeholder}
          isPlaceholder={!label}
          onClear={allowClear && value ? () => onChange(null, null) : null}
          open={open}
          onClick={() => { setQ(""); setOpen(!open); }}
          className={triggerClassName}
        />
      )}
    >
      {({ close }) => (
        <>
          <input
            autoFocus
            className="popover-search"
            placeholder="Buscar…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <div className="popover-list">
            {busy && <div className="popover-empty">Buscando…</div>}
            {!busy && !results.length && <div className="popover-empty">Sin coincidencias</div>}
            {results.map(o => (
              <div
                key={o.value}
                className={"popover-item" + (String(o.value) === String(value) ? " selected" : "")}
                onClick={() => { onChange(o.value, o); close(); }}
              >
                <div className="content">
                  <div className="lbl">{o.label}</div>
                  {o.description && <div className="desc">{o.description}</div>}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </Popover>
  );
}

// Convenience: API-backed persona combobox.
export function PersonaCombobox({ value, label, onChange, ...rest }) {
  const search = async (q) => {
    if (!q || q.length < 2) return [];
    const r = await personasApi.search(q);
    return (r.data || []).map(p => ({
      value: p.id,
      label: p.nombre,
      description: p.cedula ? `CC ${p.cedula}` : null,
    }));
  };
  return (
    <PopoverCombobox
      value={value}
      label={label}
      searchFn={search}
      onChange={onChange}
      {...rest}
    />
  );
}
